import { CoreInterfaces, DTOs } from "./../../core/Models";
import * as Constants from "./../../core/Constants";
import { dateToISO, isValidDate } from "./../utils";
import {
  formBEEmployee,
  formBEEmployeeFromContactPersonItem,
} from "./employee-mappers";

function formTaskWritePack(
  taskDTOs: Array<DTOs.TaskDTO>
): Array<CoreInterfaces.TaskConfigurationWrite> {
  const tasksPack = (taskDTOs || []).map((taskDTO) => {
    const pack: CoreInterfaces.TaskConfigurationWrite = {
      Code: taskDTO.data.code,
      Type: taskDTO.data.type,
      Title: taskDTO.data.title,
      Frequency: taskDTO.data.frequency,
      AllocationOfResponsability: taskDTO.data.allocationOfResponsibility,
      Comment: taskDTO.data.comment,
      TimeDue: taskDTO.data.timeDue,
      EstimatedTimePerUnit: taskDTO.data.estimatedTimePerUnit,
      StaffLevel: taskDTO.data.staffLevel,
      ServiceTaskGroup: taskDTO.data.serviceTaskGroup,
      SumTimeInHrsAndMinutes: taskDTO.data.sumTimeInHrsAndMinutes,
      Visibility: taskDTO.data.visibility,
      IsActive: taskDTO.state.isActive,
      Adjustments: {
        Frequency: taskDTO.data.adjustments.frequency,
        AllocationOfResponsability:
          taskDTO.data.adjustments.allocationOfResponsibility,
        Comment: taskDTO.data.adjustments.comment,
        EstimatedTimePerUnit: taskDTO.data.adjustments.estimatedTimePerUnit
          ? +taskDTO.data.adjustments.estimatedTimePerUnit
          : null,
        StaffLevel: taskDTO.data.adjustments.staffLevel,
      },
    };
    return pack;
  });
  return tasksPack;
}

function formQuestionWritePack(
  questionDTOs: Array<DTOs.QuestionDTO>
): Array<CoreInterfaces.QuestionConfigurationWrite> {
  const questionsPack = (questionDTOs || []).map((questionDTO) => {
    const pack: CoreInterfaces.QuestionConfigurationWrite = {
      Code: questionDTO.data.code,
      DefaultValue: questionDTO.data.defaultValue,
      UserInput: {
        Answer: questionDTO.data.userValue as unknown as string,
        Comment: questionDTO.data.comment,
        IsFilled: questionDTO.state.isFilled,
      },
    };
    if (
      questionDTO.data.inputType === Constants.QuestionInputType.License ||
      questionDTO.data.inputType ===
        Constants.QuestionInputType.ContactPersonUserDetails ||
      questionDTO.data.inputType ===
        Constants.QuestionInputType.MultipleSelect ||
      questionDTO.data.inputType === Constants.QuestionInputType.FullDate ||
      questionDTO.data.inputType === Constants.QuestionInputType.RangeNumber ||
      questionDTO.data.inputType ===
        Constants.QuestionInputType.ClientDetails ||
      questionDTO.data.inputType === Constants.QuestionInputType.SigneeDetails
    ) {
      pack.UserInput.Answer = JSON.stringify(questionDTO.data.userValue);
    }
    if (questionDTO.data.inputType === Constants.QuestionInputType.Date) {
      pack.UserInput.Answer = isValidDate(questionDTO.data.userValue as Date)
        ? (questionDTO.data.userValue as Date).toISOString()
        : "";
    }
    return pack;
  });
  return questionsPack;
}

function formAdditionalCostsWritePack(
  additionalCosts: Array<DTOs.AdditionalCostDTO>
): Array<CoreInterfaces.TaskCostWrite> {
  const additionalCostsToProcess: Array<DTOs.AdditionalCostDTO> =
    additionalCosts.filter(
      (additionalCost) => !additionalCost.data.isFrontendObject
    ) ?? [];
  const taskCosts: Array<CoreInterfaces.TaskCostWrite> = [];
  for (const additionalCostDTO of additionalCostsToProcess) {
    taskCosts.push({
      Cost: additionalCostDTO.data.cost,
      Code: additionalCostDTO.data.code,
      MinCost: additionalCostDTO.data.minCost,
      MaxCost: additionalCostDTO.data.maxCost,
      Adjustments: {
        Cost: additionalCostDTO.data.adjustments.Cost,
      },
    });
  }
  return taskCosts;
}

export function formServiceConfigurationWritePack(
  globalState: CoreInterfaces.AppState,
  businessOpportunityId: string,
  businessUnit: string,
  clientId: string,
  description: string,
  version: number,
  engagementLetterMetadata: CoreInterfaces.EngagementLetterMetadata,
  digitalSigningDetails: CoreInterfaces.DigitalSigningDetails,
  totalCost: number,
  engagementDocumentsLanguage: Constants.Languages = null
): CoreInterfaces.EngagementConfigurationWritePack {
  const servicesPack = globalState.services
    .filter((serviceDTO) => serviceDTO.state.isSelected)
    .map((serviceDTO) => {
      const pack: CoreInterfaces.ServiceConfigurationWrite = {
        Code: serviceDTO.data.code,
        Tasks: formTaskWritePack(serviceDTO.data.tasks),
        Questions: formQuestionWritePack(serviceDTO.data.questions),
        IsSelected: serviceDTO.state.isSelected,
        IsExpandedQuestionAccordion:
          serviceDTO.state.isExpandedQuestionAccordion,
        IsExpandedEngagementDescriptionAccordion:
          serviceDTO.state.isExpandedEngagementDescriptionAccordion,
        IsExpandedPriceAndTimeCalculationAccordion:
          serviceDTO.state.isExpandedPriceAndTimeCalculationAccordion,
        StaffingAdjustments: {
          Cost: serviceDTO.data.staffingAdjustments.cost,
          ExtendedPriceListPanelCost:
            serviceDTO.data.staffingAdjustments.extendedPriceListPanelCost,
        },
        AdditionalCosts: formAdditionalCostsWritePack(
          serviceDTO.data.additionalCosts
        ),
        ServiceRecurringType: serviceDTO.data.recurringType,
        ServiceGroup: serviceDTO.data.serviceGroup,
        InternalNotes: serviceDTO.data.internalNotes,
      };
      return pack;
    });

  const configurationWritePack: CoreInterfaces.EngagementConfigurationWritePack =
    {
      Id: globalState.currentConfiguration.id,
      EngagementLetterParentId:
        globalState.currentConfiguration.engagementLetterParentId,
      Version: version,
      Services: servicesPack,
      BusinessOpportunityId: businessOpportunityId,
      BusinessUnit: businessUnit,
      ClientId: clientId,
      Description: description,
      Status: globalState.currentConfiguration.id
        ? globalState.currentConfiguration.status
        : null,
      CompanyName: globalState.generalVariables.companyName,
      OrganisationNumber: globalState.generalVariables.organisationNo,
      TaskAllocationConfigurationVersion:
        globalState.currentConfiguration.applicationConfiguration
          ?.taskAllocationConfigurationVersion,
      TaskCostConfigurationVersion:
        globalState.currentConfiguration.applicationConfiguration
          ?.taskCostConfigurationVersion,
      RateCartConfiguration:
        globalState.currentConfiguration.applicationConfiguration
          ?.rateCartConfiguration,
      BusinessOpportunityDescription:
        globalState.remoteData.businessOpportunityInfo.Description,
      CreatedBy: globalState.currentConfiguration.createdBy,
      CreatedDate: globalState.currentConfiguration.createdDate,
      CreatedByUID: globalState.currentConfiguration.createdByUID,
      SigningDate: globalState.currentConfiguration.signingDate,
      SigningStatus: globalState.currentConfiguration.signingStatus,
      LastAccessedTime: globalState.currentConfiguration.lastAccessedTime,
      LastModifiedDate: globalState.currentConfiguration.lastModifiedDate,
      Consent: globalState.currentConfiguration.consent,
      DigitalSigningDetails: digitalSigningDetails,
      TotalCost: totalCost,
      TotalCostManualOverride:
        globalState.currentConfiguration.totalCostManualOverride,
      TotalWorkingHours: globalState.currentConfiguration.totalWorkingHours,
      CrmRowId: globalState.currentConfiguration.crmRowId,
      EngagementLetterProperties: null,
      LastUserChangesDate: !!globalState.currentConfiguration
        .lastUserChangesDate
        ? globalState.currentConfiguration.lastUserChangesDate.toISOString()
        : null,
      Owner: null,
      ValidFromDate: !!globalState.currentConfiguration.validFromDate
        ? globalState.currentConfiguration.validFromDate.toISOString()
        : null,
      ValidUntilDate: !!globalState.currentConfiguration.validUntilDate
        ? globalState.currentConfiguration.validUntilDate.toISOString()
        : null,
      IsHiddenFromImporting:
        globalState.currentConfiguration.isHiddenFromImporting,
      Language: globalState.currentConfiguration.engagementDocumentsLanguage,
      Team: globalState.currentConfiguration.team,
      NotSigningEmployeesUIDs:
        globalState.currentConfiguration.notSigningEmployeesUIDs,
    };

  if (engagementLetterMetadata) {
    configurationWritePack.EngagementLetterProperties = {
      Version: engagementLetterMetadata.version,
      CreatedBy: formBEEmployee(engagementLetterMetadata.createdBy),
      CreatedDate: dateToISO(engagementLetterMetadata.createdDate),
    };
  }
  if (globalState.currentConfiguration.owner) {
    configurationWritePack.Owner = formBEEmployeeFromContactPersonItem(
      globalState.currentConfiguration.owner
    );
  }
  if (engagementDocumentsLanguage) {
    configurationWritePack.Language = engagementDocumentsLanguage;
  }
  if (globalState.unsavedChangesTimestamp) {
    const unsavedChangesTimestamp = new Date();
    configurationWritePack.LastUserChangesDate = dateToISO(
      unsavedChangesTimestamp
    );
  }

  return configurationWritePack;
}
